/**
 * Parallel LMS Staff Management System — frontend API wrapper (Phase 10A).
 *
 * Mirrors the style of `src/pagesadmin/adminApi.ts`: one exported service
 * object of thin axios calls, all URLs built from `API_BASE_URL`, reusing the
 * shared axios instance in `src/lib/axios.ts` (CSRF + credentials already
 * handled there).
 *
 * Every response interface below was written against the ACTUAL Django view
 * source, not a summary:
 *   backend/lms/views_staff_permissions.py  (permission endpoints)
 *   backend/lms/views_staff_ops.py          (staff_ops endpoints)
 *   backend/lms/urls.py                     (exact paths, mounted at /api/lms/)
 */
import axios from '../../lib/axios';
import { isAxiosError } from 'axios';
import { API_BASE_URL } from '../../config';

/**
 * Every endpoint in this module returns its error body as `{error?: string}`
 * or `{detail?: string}` (DRF's own convention for the latter). Shared here
 * so every consumer extracts a message the same way instead of each
 * component re-deriving its own `(error as any)?.response?.data?...` --
 * PHASE B5g PORT NOTE: v2's donor components inlined that cast at every call
 * site; centralising it here is the only behavioural difference from the
 * donor, and it is purely a typing improvement (same runtime lookup, same
 * fallback order).
 */
export interface LmsApiErrorBody {
    error?: string;
    detail?: string;
}

export function getLmsApiErrorMessage(
    error: unknown,
    fallback: string
): string {
    if (isAxiosError<LmsApiErrorBody>(error)) {
        return error.response?.data?.error || error.response?.data?.detail || fallback;
    }
    return fallback;
}

/** The HTTP status code of an axios error, or `undefined` for anything else. */
export function getLmsApiErrorStatus(error: unknown): number | undefined {
    return isAxiosError(error) ? error.response?.status : undefined;
}

// ---------------------------------------------------------------------------
// Permissions (backend/lms/views_staff_permissions.py)
// ---------------------------------------------------------------------------

/**
 * LmsStaffPermission.TAB_CHOICES — backend/lms/models.py.
 *
 * `disbursed_loans` is the odd one out: it gates the standalone "Disbursed
 * Loans" sidebar screen rather than a tab inside LmsStaffOpsHub, so it
 * deliberately has NO entry in that hub's TAB_DEFS. It still appears in
 * my_permissions / the superuser permission editor, because both are driven
 * from this same server-side choices list.
 */
export type LmsTabKey =
    | 'loan_profile'
    | 'mandate_pg'
    | 'presentation_collection'
    | 'overview_disbursement'
    | 'overview_overdue_split'
    | 'calendar'
    | 'disbursed_loans';

/** GET /api/lms/admin/my_permissions/ — MyLmsPermissionsView */
export interface MyLmsPermission {
    tab: LmsTabKey;
    can_view: boolean;
    can_edit: boolean;
}

/** GET /api/lms/admin/staff_permissions/ — LmsStaffPermissionListView */
export interface LmsStaffPermissionRow {
    id: number;
    user_id: number | null;
    username: string | null;
    tab: LmsTabKey;
    can_view: boolean;
    can_edit: boolean;
    granted_by_username: string | null;
    updated_at: string | null;
}

/** GET /api/lms/admin/staff_permissions/by_user/<id>/ — adds tab_label */
export interface LmsStaffPermissionByUserRow {
    user_id: number;
    username: string;
    tab: LmsTabKey;
    tab_label: string;
    can_view: boolean;
    can_edit: boolean;
    updated_at: string | null;
}

export interface SetStaffPermissionPayload {
    user_id: number;
    tab: LmsTabKey;
    can_view: boolean;
    can_edit: boolean;
}

// ---------------------------------------------------------------------------
// Tab 1 — Active loans + loan profile (Phase 2)
// ---------------------------------------------------------------------------

/** DRF PageNumberPagination envelope (StaffOpsPagination, page_size 25) */
export interface Paginated<T> {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
}

/** GET /api/lms/staff_ops/active_loans/ — ActiveLoansListView */
export interface ActiveLoanRow {
    intellend_loan_id: string;
    lender_customer_id: string | null;
    /** DecimalField -> serialised as a string by DRF's JSON renderer */
    disbursed_amount: string | number | null;
    date_of_disbursal: string | null;
    stage_display_name: string | null;
    lender_code: string | null;
    current_status: string | null;
    /** Net disbursal amount (loan minus processing fee) from BRELoanOffers. */
    offer_loan_amt_net: string | number | null;
    /** Daily EDI repayment instalment from BRELoanOffers. */
    offer_daily_repayment: string | number | null;
    /** Total processing fee from BRELoanOffers. */
    offer_total_fee: string | number | null;
    /** Total repayment over full tenure from BRELoanOffers. */
    offer_repay_total: string | number | null;
}

/** LoanProfileView.verification_media[] */
export interface VerificationMediaItem {
    id: string;
    verification_type: string | null;
    media_type: string | null;
    taken_at: string | null;
    /** null when the signed-URL generation failed server-side */
    download_url: string | null;
}

/**
 * LoanProfileView.last_7_days_payments[] — one row per TTransaction received in
 * the last 7 IST calendar days, with its TPaymentSplit breakdown
 * (views_staff_ops.py `last_7_days_payment_rows_for_loan`). The split fields are
 * null when no TPaymentSplit row exists for that transaction.
 */
export interface LoanProfilePaymentRow {
    transaction_id: string;
    date_of_transaction: string | null;
    tran_amt: string | number | null;
    tran_category_code: number | null;
    amount_principal: string | number | null;
    amount_interest: string | number | null;
    amount_fee: string | number | null;
}

/**
 * LoanProfileView.this_month_amort — the current IST month's slice of the amort
 * schedule. `rows` reuses the shared `AmortRow` shape (same
 * `_serialize_amort_row()` as LoanAmortMandateView), and `total_amort_rows` is
 * the FULL schedule's row count so the page can label its
 * "View Full Amort Schedule" action accurately.
 */
export interface LoanProfileThisMonthAmort {
    /** YYYY-MM-DD (IST) */
    month_start: string;
    /** YYYY-MM-DD (IST) */
    month_end: string;
    rows: AmortRow[];
    total_amort_rows: number;
}

/**
 * LoanProfileView.loan_summary — the flat record behind the profile page's
 * eye-icon popup and its JSON/CSV download (`build_loan_summary()` in
 * views_staff_ops.py).
 *
 * EVERY FIELD IS NULLABLE. A loan with no LOS mapping (simulation rows,
 * lender-pushed rows, historical imports) still returns the full object with
 * nulls, so the popup opens for those loans too rather than the button being
 * dead — the UI must render its own placeholder per field.
 *
 * MONEY AND `roi` ARRIVE AS DECIMAL STRINGS, not numbers: the export must carry
 * the exact Decimal the database holds, so `Number()` is only ever applied at
 * the moment of display.
 */
export interface LoanSummary {
    cust_id: string | null;
    account_number: string | null;
    bank_name: string | null;
    customer_name: string | null;
    /** LoanApplicationMapping.loan_reference_number (then display_app_no). */
    lead_no: string | null;
    /**
     * The LENDER's application id for this loan (TLoanBaseDisbursed.lender1_loan_id).
     * This is the id Caprion quotes on CheckStatus / UpdateLead /
     * GetRepaymentDetails, so it is what staff need when raising a lender query.
     */
    application_id: string | null;
    /** Derived: "NACH + UPI" | "NACH" | "UPI" | "NONE". */
    umrn_indicator: string | null;
    /**
     * The UMRN of the ACCEPTED mandate (NACH preferred), or null when a mandate
     * exists but is still in flight — "registered" and "usable for collection"
     * are deliberately different answers here.
     */
    umrn_active: string | null;
    umrn_nach: string | null;
    umrn_upi: string | null;
    lender_name: string | null;
    /** ISO datetime — TLoanBaseDisbursed.date_of_disbursal. */
    date_of_disbursal: string | null;
    /** Decimal string. `loan_amount`, i.e. approved ≠ disbursed. */
    sanctioned_loan_amount: string | null;
    /**
     * Decimal string — the amount on the customer's LATEST BRE offer
     * (BRELoanOffers.amount). Deliberately a different figure from
     * `sanctioned_loan_amount`: what was offered vs what the lender approved.
     */
    bre_loan_amount: string | null;
    /** Decimal string. */
    amount_disbursed: string | null;
    /** Decimal string. */
    roi: string | null;
    pincode: string | null;
    /** Whole years, derived server-side from DOB. */
    age: number | null;
    email_id: string | null;
}

/**
 * The popup's row order + labels, kept in ONE place because the JSON download,
 * the CSV download and the on-screen table must all show the same fields in the
 * same order. Mirrors `LOAN_SUMMARY_FIELDS` in views_staff_ops.py.
 */
export const LOAN_SUMMARY_FIELDS: { key: keyof LoanSummary; label: string }[] = [
    { key: 'cust_id', label: 'Cust ID' },
    { key: 'account_number', label: 'AccountNumber' },
    { key: 'bank_name', label: 'Bank Name' },
    { key: 'customer_name', label: 'Name of customer' },
    { key: 'lead_no', label: 'LEAD No' },
    { key: 'application_id', label: 'Application ID' },
    { key: 'umrn_indicator', label: 'UMRN NO. Indicator' },
    { key: 'umrn_active', label: 'UMRN NO. (ACTIVE)' },
    { key: 'umrn_nach', label: 'UMRN NO. (NACH)' },
    { key: 'umrn_upi', label: 'UMRN NO. (UPI)' },
    { key: 'lender_name', label: 'Lender Name' },
    { key: 'date_of_disbursal', label: 'DateTime of disbursal' },
    { key: 'sanctioned_loan_amount', label: 'Sanctioned Loan Amount' },
    { key: 'bre_loan_amount', label: 'BRE Loan Amount' },
    { key: 'amount_disbursed', label: 'Amount Disbursed' },
    { key: 'roi', label: 'RoI' },
    { key: 'pincode', label: 'PinCode' },
    { key: 'age', label: 'Age' },
    { key: 'email_id', label: 'E-mail ID' },
];

/**
 * The "View Ext" popup record — borrower identity + business premises.
 *
 * Deliberately SEPARATE from LoanSummary rather than more keys on it: the
 * summary answers money/mandate questions, this one answers "who and where is
 * this borrower", which is what lender queries and compliance checks ask for.
 * Arrives as `loan_ext` on the same LoanProfileView response, so the popup needs
 * no request of its own. Mirrors `build_loan_ext()` in views_staff_ops.py.
 */
export interface LoanExt {
    loan_id: string | null;
    applicant_name: string | null;
    office_entity_name: string | null;
    business_address: string | null;
    state: string | null;
    pincode: string | null;
    /**
     * Residential address line 1. There is no separate landline column on the
     * model, so the address line stands in — do not read this as a phone number.
     */
    residence_no: string | null;
    /** IndividualDetail.phone_number — the KYC'd mobile. */
    mobile_no: string | null;
    /** Business address line 1, for the same reason as residence_no. */
    office_no: string | null;
    /** BusinessDetail.nature_of_business. */
    borrower_category: string | null;
    /** Derived server-side from business_constitution_type: 'Yes' | 'No'. */
    is_applicant_company: string | null;
    /** BusinessDetail.business_activity, e.g. "Grocery" / "Restaurant". */
    borrower_occupation_category: string | null;
}

/**
 * Row order + labels for the Ext popup. Same single-source-of-truth reason as
 * LOAN_SUMMARY_FIELDS: it drives the on-screen rows, the JSON key order and the
 * CSV header together. Mirrors `LOAN_EXT_FIELDS` in views_staff_ops.py.
 */
export const LOAN_EXT_FIELDS: { key: keyof LoanExt; label: string }[] = [
    { key: 'loan_id', label: 'Loan ID' },
    { key: 'applicant_name', label: 'Applicant Name' },
    { key: 'office_entity_name', label: 'Office/Name Entity' },
    { key: 'business_address', label: 'Business Address' },
    { key: 'state', label: 'State' },
    { key: 'pincode', label: 'Pincode' },
    { key: 'residence_no', label: 'Residence No' },
    { key: 'mobile_no', label: 'Mobile No' },
    { key: 'office_no', label: 'Office No' },
    { key: 'borrower_category', label: 'Borrower Category' },
    { key: 'is_applicant_company', label: 'Is Applicant Company' },
    { key: 'borrower_occupation_category', label: 'Borrower Occupation Category' },
];

/** GET /api/lms/staff_ops/loan_profile/<loan_id>/ — LoanProfileView */
export interface LoanProfile {
    intellend_loan_id: string;
    disbursed_amount: string | number | null;
    date_of_disbursal: string | null;
    /**
     * ISO datetime — `TLoanBaseDisbursed.timestamp`, i.e. when the lender's most
     * recent disbursal/RPS push was RECEIVED (re-stamped on every push and
     * webhook retry). Distinct from `date_of_disbursal`, which is the business
     * date of the disbursal. Optional so a cached/older API response still
     * type-checks; null for rows that were never pushed.
     */
    rps_received_at?: string | null;
    lender_code: string | null;
    roi: string | number | null;
    tenure: number | null;
    /** TLoanBaseDisbursed.reference_number */
    disbursement_utr: string | null;
    registered_phone: string | null;
    customer_name: string | null;
    business_name: string | null;
    cust_id: string | null;
    location: { lat: number; lng: number } | null;
    verification_media: VerificationMediaItem[];
    /**
     * The latest 'selfie_photo_and_location' entry, already present inside
     * `verification_media` and surfaced separately for the profile avatar +
     * modal. Null when the customer has no selfie on file.
     */
    selfie_media: VerificationMediaItem | null;
    /**
     * The latest 'digio_aadhar' entry with media_type 'photo' (the renderable
     * one — the 'document' variant is the full KYC PDF). Null when absent.
     */
    aadhaar_media: VerificationMediaItem | null;
    last_7_days_payments: LoanProfilePaymentRow[];
    this_month_amort: LoanProfileThisMonthAmort;
    loan_summary: LoanSummary;
    /**
     * Optional so an older/cached backend response (or a deployment where the
     * frontend ships first) renders the page normally with the Ext button simply
     * absent, instead of crashing on an undefined read.
     */
    loan_ext?: LoanExt;
}

/**
 * GET /api/lms/staff_ops/disbursed_loans/ — DisbursedLoansListView.
 *
 * Written against the view's own result dict, field for field. Note this list
 * includes CLOSED loans (unlike ActiveLoanRow, which filters terminal statuses
 * out) and adds the LOS-resolved identity columns.
 *
 * `customer_name` / `business_name` are null — not "" — whenever the loan has
 * no LoanApplicationMapping (simulation and lender-pushed loans) or the linked
 * records carry no name, so the UI must render its own placeholder.
 */
export interface DisbursedLoanRow {
    intellend_loan_id: string;
    customer_name: string | null;
    business_name: string | null;
    /** Registered phone number from IndividualDetail.phone_number (LOS user). */
    phone_number: string | null;
    /** BAP name resolved via loan_application_mapping → bap_lead → bap_fkey.name. */
    bap_name: string | null;
    /** ISO datetime — TLoanBaseDisbursed.payment_start_date (DateTimeField). */
    payment_start_date: string | null;
    /** DecimalField -> serialised as a string by DRF's JSON renderer. Actual transferred amount. */
    disbursed_amount: string | number | null;
    /**
     * Raw TLoanBaseDisbursed.loan_amount — the sanctioned/approved figure the lender sent,
     * returned WITHOUT the disbursed_amount fallback. Null when the lender omitted it.
     * Distinct from loan_amount below, which falls back to disbursed_amount for older rows.
     */
    approved_loan_amount: string | number | null;
    /** Sanctioned loan amount from the most recent LoanDetail for this user. */
    loan_amount: string | number | null;
    roi: string | number | null;
    tenure: number | null;
    date_of_disbursal: string | null;
    /** "DAILY" | "MONTHLY" in practice, but the column is a free CharField. */
    payment_freq: string | null;
    stage_display_name: string | null;
    lender_code: string | null;
    /** Net disbursal amount (loan minus processing fee) from BRELoanOffers. */
    offer_loan_amt_net: string | number | null;
    /** Daily EDI repayment instalment from BRELoanOffers. */
    offer_daily_repayment: string | number | null;
    /** Total processing fee from BRELoanOffers. */
    offer_total_fee: string | number | null;
    /** Total repayment over full tenure from BRELoanOffers. */
    offer_repay_total: string | number | null;
}

// ---------------------------------------------------------------------------
// Disbursed Loan Details — dashboard metrics
// (backend/lms/disbursal_metrics.py `build_metrics()`, served by
//  DisbursedLoansMetricsView — views_staff_ops.py:3046)
//
// EVERY MONEY FIELD BELOW IS A `string`, NOT A `number`. `disbursed_amount` is a
// Django DecimalField and the backend serialises it with `money_str()`
// (disbursal_metrics.py:150) precisely so JSON float representation error never
// touches a figure the business reads. The client must therefore call `Number()`
// only at the point of plotting or formatting, never to store the value.
// ---------------------------------------------------------------------------

export interface LoanAmountBucket {
    key: string;
    label: string;
}

export interface DisbursedLoansFilterOptions {
    lenders: string[];
    baps: string[];
    loan_amount_buckets: LoanAmountBucket[];
}

export interface ApprovedPipelineUser {
    user_id: number;
    name: string | null;
    phone: string | null;
    approved_loan_amount: string | number | null;
    loan_amount: string | number | null;
    /** Current journey stage name — only users at stage ≥ 19 (Loan Agreement) appear here. */
    stage_name?: string | null;
    last_updated: string;
    /**
     * Caprion application_id, read off the user's check_status audit rows.
     * Powers the per-row "Check Status" button; null when no such row carries
     * one, in which case the button must stay disabled rather than call the
     * lender API with an empty id.
     */
    application_id?: string | null;
}

export interface ApprovedPipelineResponse {
    count: number;
    total_approved_amount: string | null;
    results: ApprovedPipelineUser[];
}

/**
 * One point of `series.current` / `series.last_month` — `cumulative_points()`
 * (disbursal_metrics.py:240).
 *
 * Both series are ZERO-FILLED for days with no disbursals, so a gap renders as a
 * flat cumulative segment rather than the line jumping across a missing x value.
 * `amount`/`count` are that day's own figures; `cum_*` is the running total.
 */
export interface SeriesPoint {
    /** Day of the month, 1..31 — the chart's x-axis value. */
    day: number;
    /** IST calendar date, `YYYY-MM-DD`. */
    date: string;
    /** Decimal string. That day's disbursal amount. */
    amount: string;
    count: number;
    /** Decimal string. Running total from the 1st through `day`. */
    cum_amount: string;
    cum_count: number;
}

/**
 * A {amount, count, period_start, period_end} block — `_window_payload()`
 * (disbursal_metrics.py:358). Used for `mtd`, `last_month_same_period` and
 * `last_month_full`.
 *
 * `period_start`/`period_end` are NOT decoration: the comparison is
 * "same period last month, DAY-ALIGNED" (plan decision A), clamped to the
 * shorter month, so the UI must spell the two ranges out for the figure to mean
 * anything unambiguous.
 */
export interface MetricWindow {
    /** Decimal string. */
    amount: string;
    count: number;
    /** IST `YYYY-MM-DD`, inclusive. */
    period_start: string;
    /** IST `YYYY-MM-DD`, inclusive. */
    period_end: string;
}

/**
 * One entry of `run_rate` — `run_rate()` (disbursal_metrics.py:296).
 *
 * The projection EXTENDS the real month-to-date figure
 * (`MTD + daily_avg * days_remaining`), which is what lets the client draw the
 * dotted forecast starting exactly on today's real cumulative point instead of
 * as a floating separate series.
 */
export interface RunRateEntry {
    /** 7 | 14 | 21 — echoed back so the entry is self-describing. */
    window_days: number;
    /** IST `YYYY-MM-DD`. The window deliberately spans month boundaries. */
    window_start: string;
    window_end: string;
    /** Decimal string. */
    window_amount: string;
    window_count: number;
    /** Decimal string. */
    daily_avg_amount: string;
    /** A float (2dp) — loans per day, so fractional is meaningful here. */
    daily_avg_count: number;
    /** Days left in the current IST month AFTER today. 0 on the last day. */
    days_remaining: number;
    /** Decimal string. The dotted forecast line's end value (Amount mode). */
    projected_month_end_amount: string;
    /** Rounded to a whole loan — "42.7 loans" is not actionable. */
    projected_month_end_count: number;
}

/**
 * GET /api/lms/staff_ops/disbursed_loans/metrics/ — `build_metrics()`
 * (disbursal_metrics.py:368).
 *
 * TAKES NO PARAMS AND IS ALWAYS GLOBAL (plan decision F): the cards and chart
 * intentionally do NOT follow the table's search/date filters, because a
 * "Total Disbursed" card that quietly did would still be read as a portfolio
 * total. Simulation loans (`SIM_` prefix) are excluded server-side.
 */
export interface DisbursedLoanMetrics {
    /** IST ISO8601 with offset, e.g. "2026-08-04T10:30:00+05:30". */
    as_of: string;
    /** IST `YYYY-MM-DD`. The last day of `series.current`. */
    today_ist: string;
    total: {
        /** Decimal string. ALL-TIME, including rows with no disbursal date. */
        amount: string;
        count: number;
        /**
         * Rows with a null `date_of_disbursal` (lms/models.py:113): counted in
         * `total` above but absent from `mtd`, `series` and `run_rate`. Surfaced
         * so the two figures visibly reconcile on screen instead of quietly
         * disagreeing — render it as a footnote when > 0.
         */
        undated_count: number;
    };
    /** 1st of the current IST month through today. */
    mtd: MetricWindow;
    /** 1st of last month through the DAY-ALIGNED, clamped end date. */
    last_month_same_period: MetricWindow;
    /** All of last month — the chart's "Last month total" reference line. */
    last_month_full: MetricWindow;
    performance: {
        /** Decimal string; may be negative. mtd − last_month_same_period. */
        amount_delta: string;
        /**
         * **`null` when last month's same period was zero** — there is no
         * meaningful percentage growth from nothing (plan §4.3 case 3). Render
         * "—"/"New" with the absolute delta; NEVER `NaN%` or `Infinity%`.
         */
        amount_pct: number | null;
        count_delta: number;
        /** `null` on a zero baseline, exactly as `amount_pct`. */
        count_pct: number | null;
    };
    series: {
        days_in_current_month: number;
        days_in_last_month: number;
        /**
         * STOPS AT `today_ist` — real MTD data only, never zero-filled into the
         * future. The client must not extrapolate it; the forecast is a separate
         * dotted line drawn from `run_rate`.
         */
        current: SeriesPoint[];
        /** The FULL last month, so the comparison line spans the whole chart. */
        last_month: SeriesPoint[];
    };
    /**
     * Keyed by the window as a **STRING** ("7" | "14" | "21") because JSON
     * object keys always are — index with `String(selectedWindow)`.
     *
     * All three windows arrive in ONE response (they are three cheap aggregates
     * over at most 21 days), so the UI dropdown is instant and needs no refetch.
     */
    run_rate: Record<string, RunRateEntry>;
}

// ---------------------------------------------------------------------------
// Tab 2 — Amort + mandate + NSP + external PG (Phases 3 & 4)
// ---------------------------------------------------------------------------

/** _serialize_mandate() — UserMandateRegistrationMapping subset */
export interface MandateInfo {
    mandate_id: string | null;
    umrn: string | null;
    /** CharField with choices ((1,'UPI'),(2,'NACH')) — arrives as a string */
    auth_type: string | null;
    /** CharField choices ((1..5)); "4" == accepted */
    status: string | null;
}

/** _serialize_amort_row() */
export interface AmortRow {
    loan_series: number | null;
    payment_date: string | null;
    payment_amount: string | number | null;
    payment_amount_p: string | number | null;
    payment_amount_i: string | number | null;
}

/** GET /api/lms/staff_ops/loan_amort_mandate/<loan_id>/ */
export interface LoanAmortMandateResponse {
    amort_schedule: AmortRow[];
    mandate: MandateInfo | null;
}

/** MandateDetailView.presentations[] */
export interface PresentationRow {
    id: number;
    presentation_type: 'UPI' | 'NACH' | null;
    nsp_id: string | null;
    settlement_datetime: string | null;
    final_status: string | null;
    status_finalized_at: string | null;
    last_polled_at: string | null;
    created_at: string | null;
    updated_at: string | null;
}

/** MandateDetailView.pg_records[] — basic info only (no download URL) */
export interface PgRecordRow {
    id: number;
    source: string | null;
    created_at: string | null;
    has_photo: boolean;
}

/**
 * MandateDetailView.mandate_availability[<type>] — whether the customer already
 * holds a mandate of that auth_type, and therefore whether adding one is
 * permitted. `can_add` is UX affordance only; the real enforcement is
 * `assert_mandate_type_is_missing()` in backend/lms/mandate_validation.py,
 * which rejects a duplicate add with a 400 regardless of what the UI shows.
 */
export interface MandateTypeAvailability {
    exists: boolean;
    can_add: boolean;
    mandate: MandateInfo | null;
}

export interface MandateAvailability {
    upi: MandateTypeAvailability;
    nach: MandateTypeAvailability;
}

/** GET /api/lms/staff_ops/mandate/<loan_id>/ — MandateDetailView */
export interface MandateDetailResponse {
    mandate: MandateInfo | null;
    presentations: PresentationRow[];
    pg_records: PgRecordRow[];
    mandate_availability: MandateAvailability;
}

export type PresentationType = 'UPI' | 'NACH';

/** POST .../nsp/ response — MandateNspUpdateView */
export interface UpdateNspResponse {
    id: number;
    presentation_type: PresentationType | null;
    nsp_id: string | null;
    final_status: string | null;
}

/** POST .../fetch_status/ response (202) — MandateFetchStatusView */
export interface FetchStatusQueuedResponse {
    queued: boolean;
    task_id: string | null;
    id: number;
    presentation_type: PresentationType | null;
    nsp_id: string | null;
    final_status: string | null;
}

/** POST .../pg_record/ response (201) — MandatePgRecordCreateView */
export interface CreatePgRecordResponse {
    id: number;
    source: string | null;
    created_at: string | null;
    has_photo: boolean;
}

// ---------------------------------------------------------------------------
// Tab 3 — Raise presentation / lender collection (Phase 5)
// ---------------------------------------------------------------------------

/** GET .../presentation/<loan_id>/eligibility/ */
export interface PresentationEligibility {
    earliest_allowed: string;
    has_active_upi_mandate: boolean;
    has_active_nach_mandate: boolean;
}

/** PresentationListView.results[] — PresentationRow + collection audits */
export interface PresentationListRow extends PresentationRow {
    collection_audits: {
        id: number;
        is_success: boolean;
        created_at: string | null;
    }[];
}

export interface PresentationListResponse {
    results: PresentationListRow[];
}

/** raise_upi / raise_nach request body */
export interface RaisePresentationPayload {
    mode: '6am' | 'custom';
    /** YYYY-MM-DD */
    date: string;
    /** HH:MM — required when mode === 'custom' */
    time?: string;
}

/** raise_upi / raise_nach 202 response */
export interface RaisePresentationResponse {
    queued: boolean;
    task_id: string | null;
    id: number;
    presentation_type: PresentationType | null;
    nsp_id: string | null;
    settlement_datetime: string | null;
    final_status: string | null;
    raise_request_json: Record<string, unknown> | null;
}

/** update_lender_collection 202 response */
export interface UpdateLenderCollectionResponse {
    queued: boolean;
    task_id: string | null;
    presentation_id: number;
    lender_name: string | null;
}

// ---------------------------------------------------------------------------
// Tabs 4/5/6 — Overview, overdue split, calendar (Phases 7/8/9)
// ---------------------------------------------------------------------------

/** GET /api/lms/staff_ops/overview/summary/ */
export interface OverviewSummary {
    non_simulated_disbursed_count: number;
    on_time_count: number;
    missing_payment_count: number;
    as_of: string;
}

/** GET /api/lms/staff_ops/overview/missing_payments/ (paginated, flat rows) */
export interface MissingPaymentRow {
    loan_id: string;
    missing_date: string;
    amount_due: number;
    lender_customer_id: string | null;
    lender_code: string | null;
}

/** OverviewOverdueSplitView bucket entry */
export interface OverdueSplitEntry {
    intellend_loan_id: string;
    disbursed_amount: string | number | null;
    lender_customer_id: string | null;
    current_status: string | null;
}

/** GET /api/lms/staff_ops/overview/overdue_split/ */
export interface OverdueSplitResponse {
    overdue: OverdueSplitEntry[];
    not_overdue: OverdueSplitEntry[];
    overdue_count: number;
    not_overdue_count: number;
    truncated: boolean;
    max_per_bucket: number;
}

/** Calendar entry status vocabulary — views_staff_ops.py CAL_STATUS_* */
export type CalendarEntryStatus =
    | 'PAID'
    | 'PARTIALLY_PAID'
    | 'OVERDUE'
    | 'DUE'
    | 'NO_BILL_RAISED';

export interface CalendarTransaction {
    transaction_id: string | null;
    tran_amt: string | number | null;
    date_of_transaction: string | null;
    tran_category_code: string | null;
}

export interface CalendarEntry {
    intellend_loan_id: string;
    loan_series: number | null;
    payment_date: string | null;
    payment_amount: string | number | null;
    payment_amount_p: string | number | null;
    payment_amount_i: string | number | null;
    status: CalendarEntryStatus;
    net_due: number | null;
    paid_amount: number | null;
    lender_customer_id: string | null;
    current_status: string | null;
    transactions: CalendarTransaction[];
}

/** GET /api/lms/staff_ops/calendar/<date_str>/ */
export interface CalendarDayResponse {
    date: string;
    include_no_schedule: boolean;
    entries: CalendarEntry[];
    no_schedule_loans: {
        intellend_loan_id: string;
        lender_customer_id: string | null;
        current_status: string | null;
    }[];
}

// ---------------------------------------------------------------------------
// Intra-day bounce tracking
// ---------------------------------------------------------------------------

export interface IntradayBounceStatus {
    csv_last_uploaded_at: string | null;
    last_task_status: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE' | null;
    xlsx_last_generated_at: string | null;
    download_url: string | null;
    expires_in_seconds: number | null;
}

// ---------------------------------------------------------------------------
// Service object
// ---------------------------------------------------------------------------

const LMS = `${API_BASE_URL}/api/lms`;

export const lmsStaffService = {
    // ---- Permissions -----------------------------------------------------
    /** GET /api/lms/admin/my_permissions/ */
    getMyPermissions: () => axios.get<MyLmsPermission[]>(`${LMS}/admin/my_permissions/`),

    // Superuser permission-admin (consumed by Phase 10C's LmsPermissionsAdmin)
    listStaffPermissions: () =>
        axios.get<LmsStaffPermissionRow[]>(`${LMS}/admin/staff_permissions/`),
    getStaffPermissionsByUser: (userId: number) =>
        axios.get<LmsStaffPermissionByUserRow[]>(
            `${LMS}/admin/staff_permissions/by_user/${userId}/`
        ),
    setStaffPermission: (data: SetStaffPermissionPayload) =>
        axios.post<LmsStaffPermissionRow>(`${LMS}/admin/staff_permissions/set/`, data),

    // ---- Tab 1: active loans + profile ----------------------------------
    getActiveLoans: (params: { search?: string; page?: number; page_size?: number }) =>
        axios.get<Paginated<ActiveLoanRow>>(`${LMS}/staff_ops/active_loans/`, { params }),
    getLoanProfile: (loanId: string) =>
        axios.get<LoanProfile>(
            `${LMS}/staff_ops/loan_profile/${encodeURIComponent(loanId)}/`
        ),
    /**
     * The download-all endpoint streams a zip back directly
     * (Content-Type: application/zip), so it is consumed as a plain URL for an
     * <a href> / window.open rather than via axios.
     */
    getDownloadAllMediaUrl: (loanId: string) =>
        `${LMS}/staff_ops/loan_profile/${encodeURIComponent(loanId)}/download_all_media/`,
    /**
     * Same deal as getDownloadAllMediaUrl: streams a zip of the two payload
     * JSONs (<NAME>_<PHONE>_Disbursal_Payload.json + <NAME>_<PHONE>_RPS.json),
     * so it is consumed as a plain <a href> URL, not via axios.
     */
    getDownloadPayloadsUrl: (loanId: string) =>
        `${LMS}/staff_ops/loan_profile/${encodeURIComponent(loanId)}/download_payloads/`,

    // ---- Tab 2: amort + mandate + NSP + external PG ----------------------
    getLoanAmortMandate: (loanId: string) =>
        axios.get<LoanAmortMandateResponse>(
            `${LMS}/staff_ops/loan_amort_mandate/${encodeURIComponent(loanId)}/`
        ),
    getMandateDetail: (loanId: string) =>
        axios.get<MandateDetailResponse>(
            `${LMS}/staff_ops/mandate/${encodeURIComponent(loanId)}/`
        ),
    updateNspId: (
        loanId: string,
        data: { presentation_type: PresentationType; nsp_id: string }
    ) =>
        axios.post<UpdateNspResponse>(
            `${LMS}/staff_ops/mandate/${encodeURIComponent(loanId)}/nsp/`,
            data
        ),
    fetchPresentationStatus: (loanId: string, presentationId: number) =>
        axios.post<FetchStatusQueuedResponse>(
            `${LMS}/staff_ops/mandate/${encodeURIComponent(loanId)}/fetch_status/`,
            { presentation_id: presentationId }
        ),
    /** Multipart: raw_response_text (required) + photo (optional file) */
    createPgRecord: (loanId: string, formData: FormData) =>
        axios.post<CreatePgRecordResponse>(
            `${LMS}/staff_ops/mandate/${encodeURIComponent(loanId)}/pg_record/`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        ),
    /** Intentional placeholder — always 501 with an explanatory `error` body. */
    pgFetchStatus: (loanId: string) =>
        axios.post<{ error: string }>(
            `${LMS}/staff_ops/mandate/${encodeURIComponent(loanId)}/pg_fetch_status/`,
            {}
        ),

    // ---- Tab 3: raise presentation / lender collection -------------------
    getPresentationEligibility: (loanId: string) =>
        axios.get<PresentationEligibility>(
            `${LMS}/staff_ops/presentation/${encodeURIComponent(loanId)}/eligibility/`
        ),
    getPresentationList: (loanId: string) =>
        axios.get<PresentationListResponse>(
            `${LMS}/staff_ops/presentation/${encodeURIComponent(loanId)}/list/`
        ),
    raiseUpiPresentation: (loanId: string, data: RaisePresentationPayload) =>
        axios.post<RaisePresentationResponse>(
            `${LMS}/staff_ops/presentation/${encodeURIComponent(loanId)}/raise_upi/`,
            data
        ),
    raiseNachPresentation: (loanId: string, data: RaisePresentationPayload) =>
        axios.post<RaisePresentationResponse>(
            `${LMS}/staff_ops/presentation/${encodeURIComponent(loanId)}/raise_nach/`,
            data
        ),
    updateLenderCollection: (loanId: string, presentationId: number) =>
        axios.post<UpdateLenderCollectionResponse>(
            `${LMS}/staff_ops/presentation/${encodeURIComponent(loanId)}/update_lender_collection/`,
            { presentation_id: presentationId }
        ),

    // ---- Tabs 4/5/6: overview, overdue split, calendar -------------------
    getOverviewSummary: () =>
        axios.get<OverviewSummary>(`${LMS}/staff_ops/overview/summary/`),
    getOverviewMissingPayments: (params: { page?: number; page_size?: number }) =>
        axios.get<Paginated<MissingPaymentRow>>(
            `${LMS}/staff_ops/overview/missing_payments/`,
            { params }
        ),
    getOverdueSplit: () =>
        axios.get<OverdueSplitResponse>(`${LMS}/staff_ops/overview/overdue_split/`),
    getCalendarDay: (dateStr: string, includeNoSchedule: boolean) =>
        axios.get<CalendarDayResponse>(
            `${LMS}/staff_ops/calendar/${encodeURIComponent(dateStr)}/`,
            { params: { include_no_schedule: includeNoSchedule } }
        ),

    // ---- Disbursed Loan Details (standalone screen) -----------------------
    /**
     * Gated server-side by the "disbursed_loans" tab permission, so a staff
     * user without it gets a 403 here even though the sidebar entry is
     * controlled separately by the "lms-disbursed-loans" menu permission.
     */
    /**
     * `date_from`/`date_to` are IST `YYYY-MM-DD` and are INCLUSIVE on both ends
     * (the backend widens them to IST day bounds via `_ist_day_bounds()`).
     *
     * A malformed date, or `date_from > date_to`, is a **400 with an `{error}`
     * body** — deliberately, rather than a silently dropped filter or an empty
     * page, either of which would show the user a number they would believe.
     * Callers must surface `error.response.data.error`.
     */
    getDisbursedLoans: (params: {
        search?: string;
        page?: number;
        page_size?: number;
        date_from?: string;
        date_to?: string;
        lender_code?: string;
        bap_name?: string;
        loan_amount_bucket?: string;
    }) =>
        axios.get<Paginated<DisbursedLoanRow>>(`${LMS}/staff_ops/disbursed_loans/`, {
            params,
        }),
    /**
     * The KPI cards + trend chart payload.
     *
     * When `referenceMonth` is provided (format `"YYYY-MM"`), the backend
     * returns data for that full calendar month instead of the live MTD.
     * When absent/empty, the existing behaviour is preserved (live MTD).
     *
     * All three run-rate windows come back at once, so the chart's 7/14/21
     * dropdown is a pure client-side switch with no refetch.
     */
    getDisbursedLoanMetrics: (referenceMonth?: string) =>
        axios.get<DisbursedLoanMetrics>(
            `${LMS}/staff_ops/disbursed_loans/metrics/`,
            referenceMonth ? { params: { reference_month: referenceMonth } } : undefined
        ),
    /**
     * Server-side CSV of the WHOLE filtered register — no pagination, no row
     * cap. Shares `_disbursed_loans_queryset()` with the list endpoint, so the
     * file cannot disagree with the table on screen.
     *
     * `responseType: 'blob'` is REQUIRED. Without it axios would parse the
     * `text/csv` body as a string and the UTF-8 BOM the backend prepends (so
     * Excel renders Indian names rather than mojibake) would be corrupted on the
     * way to `URL.createObjectURL`.
     *
     * The response carries its own IST-stamped filename in
     * `Content-Disposition`; prefer that over inventing one client-side.
     * On a bad date range this still returns 400 JSON — but as a Blob, so the
     * caller has to read it back with `.text()` to get the message.
     */
    exportDisbursedLoans: (params: {
        search?: string;
        date_from?: string;
        date_to?: string;
        lender_code?: string;
        bap_name?: string;
        loan_amount_bucket?: string;
    }) =>
        axios.get<Blob>(`${LMS}/staff_ops/disbursed_loans/export/`, {
            params,
            responseType: 'blob',
        }),
    /** GET /api/lms/staff_ops/disbursed_loans/approved_pipeline/ */
    getApprovedPipeline: () =>
        axios.get<ApprovedPipelineResponse>(`${LMS}/staff_ops/disbursed_loans/approved_pipeline/`),
    /** GET /api/lms/staff_ops/disbursed_loans/filter_options/ */
    getDisbursedLoansFilterOptions: () =>
        axios.get<DisbursedLoansFilterOptions>(`${LMS}/staff_ops/disbursed_loans/filter_options/`),
    /** GET /api/lms/intraday-bounce/status/ */
    getIntradayStatus: () =>
        axios.get<IntradayBounceStatus>(`${API_BASE_URL}/api/lms/intraday-bounce/status/`),

    /** POST /api/lms/intraday-bounce/upload/ — multipart: demands_csv */
    uploadIntradayCsv: (csvFile: File) => {
        const formData = new FormData();
        formData.append('demands_csv', csvFile);
        return axios.post<{ status: string; message: string }>(
            `${API_BASE_URL}/api/lms/intraday-bounce/upload/`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
    },

    /** POST /api/lms/intraday-bounce/generate/ — trigger XLSX generation from stored CSV */
    triggerIntradayGenerate: () =>
        axios.post<{ status: string; message: string }>(
            `${API_BASE_URL}/api/lms/intraday-bounce/generate/`,
        ),
};

export default lmsStaffService;
